import { FormInstance, FormItemProps } from 'antd/es/form';

import { MenuContext, MenuType } from '@/store/PIDEditing/PIDEditStore';
import { DeviceType } from '@/constants/deviceTypeEnum';

export const defaultDeviceModels = {
  [DeviceType.DewpointSensor]: '08e1584d-c9c1-4db0-9a6d-09678bc5f4b3',
  [DeviceType.PowerSensor]: '6ca1cb09-a2a6-4a04-9f2d-c63686161798',
  [DeviceType.PressureSensor]: 'c982068a-03f9-425d-a5b2-968faac4511d',
  [DeviceType.FlowSensor]: '7ab55a38-e207-4df6-8b46-8862bf37ad4f',
  [DeviceType.SpillValve]: 'ad0fed4e-035b-4ff1-9327-998774969d30',
  [DeviceType.AirFilter]: '065b4e80-f141-4f34-986f-1a12752ce19a',
  [DeviceType.FlowAndPressureSensor]: '721a7a51a536d575f3815',
  [DeviceType.DewpointAndPressureSensor]: '4b76b818-6f1b-4fe1-9a87-9672c793bc0c',
  [DeviceType.AirTemperatureSensor]: '83d3d85c-c759-417d-bc6e-4f00a82d2759',
  [DeviceType.Connector]: '4f76a98f-9dbe-4205-a8e4-8f2f0a6623ee',
};

type FormFieldType = 'input' | 'select';

interface FormFieldBase {
  type: FormFieldType;
  name: string;
  label: string;
  required?: boolean;
  rules?: FormItemProps['rules'];
  isAdvanced?: boolean; // Mark fields as advanced/expandable
}

interface InputField extends FormFieldBase {
  type: 'input';
  placeholder?: string;
}

interface DeviceDetailsFormValues {
  deviceType: DeviceType;
  name: string;
  description?: string;
  model: string;
  controller?: string;
}

export interface SelectField extends FormFieldBase {
  type: 'select';
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  onChange?: (value: string, form: FormInstance) => void;
}

export type FormField = InputField | SelectField;

const EDIT_DEVICE_CONFIGS = {
  [DeviceType.FlowSensor]: {
    title: 'Flow Sensor Details',
    onSubmit: values => console.log('Flow Sensor submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.PressureSensor]: {
    title: 'Pressure Sensor Details',
    onSubmit: values => console.log('Pressure Sensor submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.DewpointSensor]: {
    title: 'Dew Point Sensor Details',
    onSubmit: values => console.log('Dew Point Sensor submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.AirTemperatureSensor]: {
    title: 'Air Temperature Sensor Details',
    onSubmit: values => console.log('Air Temperature Sensor submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.FlowAndPressureSensor]: {
    title: 'Flow & Pressure Sensor Details',
    onSubmit: values => console.log('Flow & Pressure Sensor submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.DewpointAndPressureSensor]: {
    title: 'Dew Point & Pressure Sensor Details',
    onSubmit: values => console.log('Dew Point & Pressure Sensor submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.SpillValve]: {
    title: 'Spill Valve Details',
    onSubmit: values => console.log('Spill Valve submitted:', values),
    submitButtonText: 'Update',
  },
  [DeviceType.AirFilter]: {
    title: 'Air Filter Details',
    onSubmit: values => console.log('Air Filter submitted:', values),
    submitButtonText: 'Update',
  },
};

const EDIT_DEFAULT_CONFIG = {
  title: 'Device Details',
  onSubmit: values => console.log('Device submitted:', values),
  submitButtonText: 'Update',
};

export const CREATE_DEVICE_CONFIGS = {
  [DeviceType.FlowSensor]: {
    title: 'Create Flow Sensor',
  },
  [DeviceType.PressureSensor]: {
    title: 'Create Pressure Sensor',
  },
  [DeviceType.DewpointSensor]: {
    title: 'Create Dew Point Sensor',
  },
  [DeviceType.AirTemperatureSensor]: {
    title: 'Create Air Temperature Sensor',
  },
  [DeviceType.FlowAndPressureSensor]: {
    title: 'Create Flow & Pressure Sensor',
  },
  [DeviceType.DewpointAndPressureSensor]: {
    title: 'Create Dew Point & Pressure Sensor',
  },
  [DeviceType.SpillValve]: {
    title: 'Create Spill Valve',
  },
  [DeviceType.AirFilter]: {
    title: 'Create Air Filter',
  },
};

export const CREATE_DEFAULT_CONFIG = {
  title: 'Create Device',
};

export interface DynamicFormCardProps {
  title: string;
  fields: FormField[];
  onSubmit?: (values: DeviceDetailsFormValues) => void;
  onCancel?: () => void;
  submitButtonText?: string;
  initialValues?: Partial<DeviceDetailsFormValues>;
  form?: FormInstance;
  moreButtonText?: string;
  onValuesChange?: (changedValues: Partial<DeviceDetailsFormValues>, allValues: DeviceDetailsFormValues) => void;
  isLoading?: boolean;
}

const defaultModelSpecOptions = [
  { value: 'FSX-120', label: 'FSX-120' },
  { value: 'FSX-240', label: 'FSX-240' },
  { value: 'FSX-360', label: 'FSX-360' },
  { value: 'FSX-480', label: 'FSX-480' },
];

export const setDeviceDetailsProps = (
  menuType: MenuType,
  context: MenuContext,
  onCancel: () => void,
  onSubmit: (values: DeviceDetailsFormValues) => void,
  modelSpecOptions: { value: string; label: string }[] = defaultModelSpecOptions,
  isLoading = false,
  modelPlaceholder = 'Select Model',
  controllerPlaceholder = 'Select Controller',
  controllerOptions: { value: string; label: string }[] = [],
  isModelSpecDisabled = false,
  isControllerDisabled = false,
): DynamicFormCardProps => {
  const { deviceType } = context;
  const entity = menuType === MenuType.DeviceDetails && context.entity;
  const commonFields: FormField[] = [
    {
      type: 'input',
      name: 'name',
      label: 'Name',
      required: true,
    },
    {
      type: 'input',
      name: 'description',
      label: '',
      placeholder: 'Add description (optional)',
    },
    {
      type: 'select',
      name: 'model',
      label: 'Model',
      options: modelSpecOptions,
      required: true,
      placeholder: menuType === MenuType.CreateDevice ? 'Select Model Spec' : modelPlaceholder,
      loading: isLoading,
      disabled: isModelSpecDisabled,
    },
    {
      type: 'select',
      name: 'controller',
      label: 'Controller',
      options: controllerOptions,
      disabled: isControllerDisabled,
      required: false,
      placeholder: controllerPlaceholder,
      loading: isLoading,
    },
  ];

  if (deviceType === DeviceType.FlowSensor) {
    commonFields.push(
      {
        type: 'input',
        name: 'pipeDiameter',
        label: 'Pipe Diameter',
        required: false,
      },
      {
        type: 'input',
        name: 'pipeMaterial',
        label: 'Pipe Material',
        required: false,
        placeholder: 'Enter pipe material',
      },
    );
  }

  const defaultInitialValues = commonFields
    .filter((field): field is SelectField => field.type === 'select' && field.options?.length > 0)
    .reduce((acc, field) => {
      if (menuType === MenuType.CreateDevice && field.name === 'model') {
        return acc;
      }
      return { ...acc, [field.name]: field.options[0].value };
    }, {});

  let initialValues = { ...defaultInitialValues, deviceType: deviceType };
  if (menuType === MenuType.DeviceDetails && entity) {
    initialValues = {
      ...initialValues,
      name: entity.name,
      ...(deviceType === DeviceType.FlowSensor && {
        pipeDiameter: entity.pipeDiameter,
        pipeMaterial: entity.pipeMaterial,
      }),
    };
  }

  const commonProps = {
    onCancel,
    initialValues,
    fields: commonFields,
  };

  let specificProps;
  if (menuType === MenuType.CreateDevice) {
    specificProps = {
      ...(CREATE_DEVICE_CONFIGS[deviceType] || CREATE_DEFAULT_CONFIG),
      submitButtonText: 'Create Device',
    };
  } else {
    specificProps = EDIT_DEVICE_CONFIGS[deviceType] || EDIT_DEFAULT_CONFIG;
  }

  return {
    ...commonProps,
    ...specificProps,
    onSubmit,
    isLoading,
  };
};
